import { useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { faDownload, faFileImport } from '@fortawesome/pro-solid-svg-icons'
import { isArray } from 'lodash'

import SmartTable, { SmartTableDataTypes } from '../../../Components/shared/SmartTable'
import { dateStringOrNull, downloadFile, SmartTableInputTypes, validateFormInput } from '../../../utils'
import { Countries, EUROPEAN_DATE, Genders, LanguageLocales } from '../../../utils/constants'
import { selectLanguages, selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import { isAdmin, isCoordinator, isEncoder } from '../../../utils/roles'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import StudentsImportModal from '../../../Components/school/StudentsImportModal'

const OldStudentsTable = ({ additionalActions, additionalExports, exports, loading, sections, students, onDataAdd, onDataEdit, onDataDelete, reload }) => {
  const languages = useSelector(selectLanguages)
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [downloadingTemplate, setDownloadingTemplate] = useState(false)
  const [uploadModalDisplayed, setUploadModalDisplayed] = useState(false)

  const permissions = useMemo(() => ({
    canSeeNiss: isAdmin(user) || isEncoder(user) || isCoordinator(user),
    canEditStudent: isAdmin(user) || isEncoder(user) || isCoordinator(user),
    canResetPassword: isAdmin(user) || isEncoder(user),
    hasEncoderPermissions: isEncoder(user),
    hasCoordinatorPermissions: isCoordinator(user)
  }), [user])

  const localeLanguages = useMemo(() => (languages == null && user?.language == null)
    ? []
    : languages
      .map(({ code, name }) => ({
        id: code,
        name: t(name)
      }))
      .sort((a, b) => {
        const defaultLanguage = user.language ?? LanguageLocales.EN
        let sortValue

        if (a.id === defaultLanguage) {
          sortValue = -1
        } else if (b.id === defaultLanguage) {
          sortValue = 1
        } else {
          sortValue = 0
        }

        return sortValue
      })
  , [languages, user?.language])

  const data = useMemo(() => {
    let data = []

    if (isArray) {
      data = students.map(s => ({
        id: s.id,
        section: s.sections && s.sections.length > 0 ? s.sections[0].id : null,
        lastname: s.lastname,
        firstname: s.firstname,
        language: s.language,
        email: s.email,
        matricule: s.matricule,
        phoneNumber: s.phoneNumber,
        user: s.user,
        lastLogin: dateStringOrNull(s.lastLogin, EUROPEAN_DATE, 'date'),
        birthdate: dateStringOrNull(s.birthdate, EUROPEAN_DATE, 'date'),
        niss: s.niss ?? null,
        gender: s.gender ?? null,
        genderReadOnlyName: ' ',
        nationality: s.nationality ?? null,
        nationalityReadOnlyName: ' ',
        isActive: s.isActive ? t('misc.yes') : t('misc.no')
      }))
    }

    return data
  }, [students])

  const columns = useMemo(() => {
    const columns = [
      { type: SmartTableDataTypes.ID, key: 'id' },
      {
        type: SmartTableDataTypes.SELECT,
        name: t('common.section'),
        key: 'section',
        options: sections
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Lastname'),
        key: 'lastname',
        validate: data => validateFormInput(SmartTableInputTypes.NAME, data, true)
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Firstname'),
        key: 'firstname',
        validate: data => validateFormInput(SmartTableInputTypes.NAME, data, true)
      },
      {
        type: SmartTableDataTypes.SELECT,
        name: t('Language'),
        key: 'language',
        options: localeLanguages,
        preventAutoSelectDefaultValue: false
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Email'),
        key: 'email',
        validate: data => validateFormInput(SmartTableInputTypes.EMAIL, data, true)
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Registration number'),
        key: 'matricule',
        isNullable: true,
        validate: data => validateFormInput(SmartTableInputTypes.FREE_TEXT, data, false)
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Phone'),
        key: 'phoneNumber',
        validate: data => validateFormInput(SmartTableInputTypes.FREE_TEXT, data, false)
      },
      {
        type: SmartTableDataTypes.DATE,
        name: t('Birthdate'),
        key: 'birthdate',
        format: 'DD/MM/YYYY',
        isNullable: true,
        validate: data => validateFormInput(SmartTableInputTypes.FREE_TEXT, data, false)
      },
      {
        type: SmartTableDataTypes.SELECT,
        name: t('Gender'),
        key: 'gender',
        options: Object.entries(Genders).map(([key, value]) => ({ id: key, name: t(value) }))
      },
      {
        type: SmartTableDataTypes.SELECT,
        name: t('Nationality'),
        key: 'nationality',
        options: Object.entries(Countries).map(([key, value]) => ({ id: key, name: t(value) }))
      }
    ]

    if (permissions.canSeeNiss) {
      columns.push(
        {
          type: SmartTableDataTypes.STRING,
          name: t('NISS'),
          key: 'niss',
          validate: data => validateFormInput(SmartTableInputTypes.FREE_TEXT, data, false),
          password: true
        }
      )
    }

    if (permissions.hasCoordinatorPermissions) {
      columns.push(
        {
          type: SmartTableDataTypes.DATE,
          name: t('Last login'),
          key: 'lastLogin',
          format: 'DD/MM/YYYY',
          disabled: true
        }
      )
      columns.push(
        {
          type: SmartTableDataTypes.STRING,
          name: t('Active'),
          key: 'isActive'
        }
      )
    }

    return columns
  }, [t, user, sections, permissions, localeLanguages])

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true)
    await downloadFile('/students/download', 'students_template.xlsx', user)
    setDownloadingTemplate(false)
  }

  return (
    <>
      <SmartTable
        additionalExports={additionalExports}
        columns={columns}
        data={data}
        exports
        onDataAdd={onDataAdd}
        onDataEdit={onDataEdit}
        onDataDelete={onDataDelete}
        loading={loading}
        noDataText={t('You do not have any student.')}
        addDataText={t('Add a student')}
        secondaryButtons={permissions.hasEncoderPermissions
          ? [
            <IconButton
              className='mr-10'
              key='download-students-button'
              loading={downloadingTemplate}
              onClick={handleDownloadTemplate}
              icon={faDownload}
              text={t('Download template')}
            />,
            <IconButton
              className='mr-10'
              key='import-students-button'
              loading={downloadingTemplate}
              onClick={() => setUploadModalDisplayed(true)}
              icon={faFileImport}
              text={t('Import students')}
            />
          ]
          : null}
        showSecondaryButtonsInFront
        additionalActions={additionalActions}
      />
      <StudentsImportModal
        displayed={uploadModalDisplayed}
        onClose={() => setUploadModalDisplayed(false)}
        reloadStudentsAfterImport={reload}
      />
    </>
  )
}

export default OldStudentsTable
